import { ChatBot } from '@/components/shared/ChatBot';
import {
  TeamOnboardingQuestions,
  useTeamOnboardingAgent,
} from '@/hooks/useTeamOnboardingAgent';
import { useTeams } from '@/hooks/useTeams';
import { getActiveTeamQueryKey } from '@/utils/query-keys';
import { yupResolver } from '@hookform/resolvers/yup';
import { QueryObserver, useQueryClient } from '@tanstack/react-query';
import { ReactElement, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

const validationSchema = yup.object({
  description: yup.string().nullable().required('Description is required'),
  tone_and_style: yup
    .string()
    .nullable()
    .required('Tone and style is required'),
  industry: yup.string().nullable().required('Industry is required'),
  audience: yup.string().nullable().required('Audience is required'),
  posting_frequency: yup
    .number()
    .nullable()
    .required('Posting frequency is required'),
  brand_voice: yup.string().nullable().required('Brand voice is required'),
  website_url: yup.string().nullable().required('Website URL is required'),
});

export const TeamOnboardingBot = (): ReactElement | null => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const queryClient = useQueryClient();
  const { updateTeam, activeTeam } = useTeams();

  const [isFinalising, setIsFinalising] = useState(false);

  const { setValue, watch } = useForm<TeamOnboardingQuestions>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      description: activeTeam.data?.description || '',
      tone_and_style: activeTeam.data?.tone_and_style || '',
      industry: activeTeam.data?.industry || '',
      audience: activeTeam.data?.audience || '',
      posting_frequency: activeTeam.data?.posting_frequency || 0,
      brand_voice: activeTeam.data?.brand_voice || '',
      website_url: activeTeam.data?.website_url || '',
    },
  });

  const handleSubmit = async (data: TeamOnboardingQuestions): Promise<void> => {
    try {
      setIsFinalising(true);

      await updateTeam.mutateAsync({
        id: activeTeam.data?.id || '',
        ...data,
      });

      // This allows us to wait for the active team to be fetched again before we show the toast
      await new Promise<void>((resolve) => {
        const observer = new QueryObserver(queryClient, {
          queryKey: getActiveTeamQueryKey(),
        });

        const unsubscribe = observer.subscribe((result) => {
          if (!result.isFetching) {
            unsubscribe();
            resolve();
          }
        });
      });

      toast.success(
        `Team onboarding complete!\nYou can update these anytime via the sidebar panel.`,
      );
      setIsFinalising(false);
    } catch (error) {
      setIsFinalising(false);
      console.error('Failed to onboard team:', error);
      toast.error('Failed to onboard team. Please try again.');
    }
  };

  const formValues = watch();

  const { visibleMessages, appendMessage, isLoading } = useTeamOnboardingAgent({
    setValue,
    formValues,
    inputRef,
    validationSchema,
    handleSubmit,
  });

  return (
    <ChatBot
      visibleMessages={visibleMessages}
      appendMessage={appendMessage}
      inputRef={inputRef}
      isLoading={isLoading}
      overlayLoader={{
        message:
          'Onboarding your team, this may take a minute while we gather more information about your team...',
        isLoading: isFinalising,
      }}
    />
  );
};
